'use client'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { Divider } from '@/components/ui/divider'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { ProductByStyleFormatter } from '@/types/product-detail'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'

type Props = {
  data: ProductByStyleFormatter
}

// Extend product type với các trường cần thiết
type ProductWithExtraFields = {
  seo_alias: string
  title: string
  regular_price: number
  compare_price?: number
  image: string // Thêm trường image
  sale_percent?: number // Thêm trường sale_percent
  id: string
}

export const ShopTheLookItem: React.FC<Props> = ({ data }) => {
  const t = useTranslations('button')

  return (
    <ResponsiveModal>
      <ResponsiveModalTrigger asChild>
        <button
          className='group relative cursor-pointer overflow-hidden rounded-2xl'
          ga-tracking-value={`shop-the-look__open-modal__${data.slug}`}
          ga-tracking-label={data.title}
        >
          <Image
            src={ImageHelper.getImageUrl(data.thumbnail, '', 870)}
            alt={data.title}
            width={435}
            height={596}
            loading='lazy'
            className='w-full object-cover transition-transform duration-300 group-hover:scale-105'
            unoptimized
          />
          <Button
            variant='white'
            size={'large'}
            className='absolute bottom-4 left-3 max-lg:h-10 max-lg:px-6 max-lg:text-sm lg:bottom-6 lg:left-4'
          >
            {t('buy_now')}
          </Button>
        </button>
      </ResponsiveModalTrigger>
      <ResponsiveModalContent
        className='z-[103] gap-0 border-none px-5 py-6 lg:h-[600px] lg:max-h-none lg:w-[1000px] lg:max-w-none lg:rounded-md lg:px-9 lg:py-10'
        aria-describedby={'modal-login'}
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        <PreviewProductsModalContent data={data} />
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

const PreviewProductsModalContent: React.FC<Props> = ({ data }) => {
  const t = useTranslations('button')

  return (
    <div className='flex h-full flex-col gap-3 lg:flex-row lg:gap-6'>
      <picture className='max-lg:basis-2/5'>
        {' '}
        <Image
          src={ImageHelper.getImageUrl(data.thumbnail, '', 776)}
          alt={data.title}
          width={388}
          height={520}
          loading='lazy'
          className='h-full rounded-2xl object-cover object-top max-lg:h-48 max-lg:w-full max-lg:object-top'
        />
      </picture>
      <Divider className='my-2 lg:hidden' />
      <ul className='scrollbar max-h-[320px] space-y-5 overflow-y-auto pr-1 max-lg:flex-1 lg:max-h-[520px]'>
        {data.products.map((product) => {
          // Cast product to include required fields
          const typedProduct = product as unknown as ProductWithExtraFields
          return (
            <li
              className='flex items-stretch gap-3 lg:gap-6'
              key={typedProduct.seo_alias}
            >
              <Link
                href={`/product/${typedProduct.seo_alias}`}
                className='block'
                ga-tracking-value={`shop-the-look__modal__view-product-image__${typedProduct.id}`}
                ga-tracking-label={typedProduct.title}
              >
                <Image
                  src={ImageHelper.getImageUrl(typedProduct.image, '', 240)}
                  alt={data.title}
                  width={120}
                  height={160}
                  loading='lazy'
                  className='h-full min-w-20 rounded-lg object-cover max-lg:h-28 max-lg:w-20 max-lg:object-top'
                />
              </Link>
              <div className='flex flex-1 flex-col justify-between'>
                <div>
                  <Link
                    href={`/product/${typedProduct.seo_alias}`}
                    className='line-clamp-2 font-sans text-sm font-medium text-neutral-900 hover:text-primary lg:text-xl'
                    ga-tracking-value={`shop-the-look__modal__view-product-title__${typedProduct.id}`}
                    ga-tracking-label={typedProduct.title}
                  >
                    {typedProduct.title}
                  </Link>
                  <div className='mt-2 lg:mt-4'>
                    {typedProduct.compare_price &&
                    typedProduct.compare_price !==
                      typedProduct.regular_price ? (
                      <del className='mb-0.5 font-sans text-xs leading-none text-neutral-400 lg:mb-2 lg:text-base'>
                        {formatPrice(typedProduct.compare_price ?? 0)}
                      </del>
                    ) : null}
                    <span className='flex items-center gap-2 font-sans text-sm font-medium leading-none text-neutral-900 lg:text-2xl'>
                      {formatPrice(typedProduct.regular_price ?? 0)}
                      {typedProduct.sale_percent ? (
                        <TagThumbnail
                          title={`-${typedProduct.sale_percent}%`}
                          className='max-lg:h-4 max-lg:px-1.5'
                        />
                      ) : null}
                    </span>
                  </div>
                </div>
                <div className='mt-3 flex justify-end'>
                  <Button
                    variant={'primary'}
                    asChild
                    className='max-lg:h-8 max-lg:text-xs'
                  >
                    <Link
                      href={`/product/${typedProduct.seo_alias}`}
                      className='ml-auto items-center gap-2'
                      ga-tracking-value={`shop-the-look__modal__buy-now__${typedProduct.id}`}
                      ga-tracking-label={t('buy_now')}
                    >
                      {t('buy_now')}
                      <Image
                        src={'/icons/icon-arrow-line-right.svg'}
                        width={16}
                        height={10}
                        alt='icon arrow right'
                      />
                    </Link>
                  </Button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
